import { createRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type { TenantMemberListResult } from './types/member';
import { getOpenPlatformToken } from '@/services/openPlatformToken';
import { getCurrentUser } from '@/utils/currentMember';

const request = createRequest(
  'https://open-api.zhiyueyun.com/api-gateway/organization-admin',
);
const MEMBER_LIST_CACHE_PREFIX = 'announcement-member-list:';

export interface GetMemberListParams {
  pageNo?: number;
  pageSize?: number;
}

interface TenantMemberListResponse {
  data?: {
    list?: TenantMemberListResponseItem[];
    records?: TenantMemberListResponseItem[];
    items?: TenantMemberListResponseItem[];
    total?: number;
    totalElements?: number;
    pageNo?: number;
    pageNum?: number;
    pageNumber?: number;
    pageSize?: number;
  };
  list?: TenantMemberListResponseItem[];
  records?: TenantMemberListResponseItem[];
  items?: TenantMemberListResponseItem[];
  total?: number;
  totalElements?: number;
  pageNo?: number;
  pageNum?: number;
  pageNumber?: number;
  pageSize?: number;
}

type TenantMemberListResponseItem = Omit<
  TenantMemberListResult['items'][number],
  'id'
> & {
  id?: string;
  memberUuid?: string;
};

const normalizeMember = (
  member: TenantMemberListResponseItem,
): TenantMemberListResult['items'][number] => ({
  ...member,
  id: member.id || member.memberUuid || '',
});

const getMemberListCacheKey = (
  organizationUuid: string,
  pageNo: number,
  pageSize: number,
) => `${MEMBER_LIST_CACHE_PREFIX}${organizationUuid}:${pageNo}:${pageSize}`;

const readMemberListCache = (key: string): TenantMemberListResult | null => {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const value = JSON.parse(rawValue) as TenantMemberListResult;
    if (!Array.isArray(value.items) || typeof value.total !== 'number') {
      sessionStorage.removeItem(key);
      return null;
    }

    return value;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
};

const writeMemberListCache = (
  key: string,
  value: TenantMemberListResult,
) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 缓存失败不影响成员查询
  }
};

export const clearMemberListCache = () => {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(MEMBER_LIST_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // 缓存清理失败不影响登出和切换组织
  }
};

export async function getMemberList(
  params?: GetMemberListParams,
  options?: ApiRequestOptions,
) {
  const mergedParams = {
    pageNo: 1,
    pageSize: 100,
    ...(params || {}),
  };

  const organizationUuid = getCurrentUser()?.organizationUuid?.trim();
  const cacheKey = organizationUuid
    ? getMemberListCacheKey(
        organizationUuid,
        mergedParams.pageNo,
        mergedParams.pageSize,
      )
    : null;
  if (cacheKey) {
    const cachedResult = readMemberListCache(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }
  }

  const currentUser = getCurrentUser();
  const token = currentUser ? getOpenPlatformToken(currentUser) : null;
  if (!token) {
    throw new Error('开放平台令牌不可用');
  }

  const headers = {
    ...(options?.headers || {}),
    Authorization: `Bearer ${token}`,
  } as Record<string, string>;

  const result = await request<TenantMemberListResponse>('/api/v1/members', {
    ...(options || {}),
    method: 'GET',
    params: mergedParams,
    headers,
  });

  const payload = result.data || result;

  const memberListResult = {
    items: (payload.items || payload.records || payload.list || []).map(
      normalizeMember,
    ),
    total: payload.total || payload.totalElements || 0,
    pageNo:
      payload.pageNo || payload.pageNum || payload.pageNumber || mergedParams.pageNo,
    pageSize: payload.pageSize || mergedParams.pageSize,
  } satisfies TenantMemberListResult;

  if (cacheKey) {
    writeMemberListCache(cacheKey, memberListResult);
  }

  return memberListResult;
}
